import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Mail, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n, { uiLocale } from "@/i18n";
import { formatMoneyCompact } from "@/lib/format";

interface SourceData {
  source: string;
  count: number;
  revenue: number;
}

interface CampaignData {
  utm_campaign: string;
  count: number;
  revenue: number;
  conversion: number;
}

interface NewsletterStats {
  subscribers: number;
  growthLastMonth: number;
}

interface MarketingStatsProps {
  bySource: SourceData[];
  topCampaigns: CampaignData[];
  newsletterStats: NewsletterStats;
}

const SOURCE_LABELS: Record<string, string> = {
  web_form: i18n.t("system.analytics.marketing.sources.webForm"),
  referral: i18n.t("system.analytics.marketing.sources.referral"),
  admin: i18n.t("system.analytics.marketing.sources.admin"),
  phone: i18n.t("system.analytics.marketing.sources.phone"),
  email: i18n.t("system.analytics.marketing.sources.email"),
  social: i18n.t("system.analytics.marketing.sources.social"),
  other: i18n.t("system.analytics.marketing.sources.other"),
};

export default function MarketingStats({
  bySource,
  topCampaigns,
  newsletterStats,
}: MarketingStatsProps) {
  const { t } = useTranslation();
  const sourceChartData = bySource.map((s) => ({
    source: SOURCE_LABELS[s.source] || s.source,
    count: s.count,
    revenue: s.revenue,
  }));

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Newsletter Card */}
        <div className="card flex flex-col items-center justify-center">
          <div className="mb-2 rounded-full bg-blue-100 p-4 dark:bg-blue-900/30">
            <Mail className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {t("system.analytics.marketing.newsletterSubscribers")}
          </p>
          <p className="mt-1 text-4xl font-bold text-gray-900 dark:text-white">
            {newsletterStats.subscribers.toLocaleString(uiLocale())}
          </p>
          {newsletterStats.growthLastMonth > 0 && (
            <div className="mt-2 flex items-center gap-1 text-green-600">
              <TrendingUp className="h-4 w-4" />
              <span className="text-sm font-medium">
                {t("system.analytics.marketing.growthThisMonth", {
                  count: newsletterStats.growthLastMonth,
                })}
              </span>
            </div>
          )}
        </div>

        {/* Sources Chart */}
        <div className="card lg:col-span-2">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("system.analytics.marketing.ordersBySource")}
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={sourceChartData}
                layout="vertical"
                margin={{ top: 5, right: 20, left: 100, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12, fill: "#6b7280" }} />
                <YAxis
                  type="category"
                  dataKey="source"
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  width={95}
                />
                <Tooltip
                  formatter={(value: number, name) => [
                    name === "count"
                      ? t("system.analytics.marketing.ordersCount", { count: value })
                      : formatMoneyCompact(value),
                    name === "count"
                      ? t("system.analytics.marketing.countLabel")
                      : t("system.analytics.marketing.revenueLabel"),
                  ]}
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                  }}
                />
                <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} name="count" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Top Campaigns */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          {t("system.analytics.marketing.topUtm")}
        </h3>
        {topCampaigns.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t("system.analytics.marketing.campaign")}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t("system.analytics.marketing.orders")}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t("system.analytics.marketing.revenueLabel")}
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                    {t("system.analytics.marketing.conversion")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {topCampaigns.map((campaign) => (
                  <tr
                    key={campaign.utm_campaign}
                    className="hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    <td className="whitespace-nowrap px-4 py-3">
                      <span className="font-medium text-gray-900 dark:text-white">
                        {campaign.utm_campaign}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-center">
                      <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-sm font-medium text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                        {campaign.count}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                      {formatMoneyCompact(campaign.revenue)}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-center">
                      <span className="rounded bg-green-100 px-2 py-0.5 text-sm font-medium text-green-800 dark:bg-green-900/30 dark:text-green-400">
                        {campaign.conversion}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="py-8 text-center text-gray-500">{t("system.analytics.marketing.noUtm")}</p>
        )}
      </div>

      {/* Revenue by Source Table */}
      <div className="card">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          {t("system.analytics.marketing.revenueBySource")}
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t("system.analytics.marketing.source")}
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t("system.analytics.marketing.orders")}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t("system.analytics.marketing.revenueLabel")}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  {t("system.analytics.marketing.avgValue")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {bySource.map((source) => (
                <tr key={source.source} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="whitespace-nowrap px-4 py-3 font-medium text-gray-900 dark:text-white">
                    {SOURCE_LABELS[source.source] || source.source}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-center text-gray-600 dark:text-gray-400">
                    {source.count}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right font-medium text-gray-900 dark:text-white">
                    {formatMoneyCompact(source.revenue)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-gray-600 dark:text-gray-400">
                    {source.count > 0
                      ? formatMoneyCompact(Math.round(source.revenue / source.count))
                      : "–"}
                  </td>
                </tr>
              ))}
              {bySource.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                    {t("system.analytics.noData")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
